import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, Row, Col, Typography, Tag, Space, Button,
    Descriptions, Table, Divider, Steps, Progress, Statistic,
    notification, Spin, Empty, Timeline, Badge, Alert
} from 'antd';
import {
    ArrowLeftOutlined, PrinterOutlined, FileTextOutlined,
    UserOutlined, BarChartOutlined, SafetyCertificateOutlined,
    EditOutlined, CheckCircleFilled, ClockCircleOutlined
} from '@ant-design/icons';
import {
    ClipboardList,
    ShieldCheck,
    Hammer,
    Zap,
    Flag,
    CheckCircle2,
    Clock
} from 'lucide-react';
import { orderService } from '../services/orderService';
import { customerService } from '../services/customerService';
import ProformaGenerator from './ProformaGenerator';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';

dayjs.extend(minMax);

const { Title, Text } = Typography;

const OrderDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isProformaVisible, setIsProformaVisible] = useState(false);
    const [customers, setCustomers] = useState([]);

    useEffect(() => {
        fetchOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchOrder = async () => {
        try {
            setLoading(true);
            const res = await orderService.getOrderById(id);
            if (res.success) setOrder(res.data);

            // Also fetch customers for proforma
            const custRes = await customerService.getAllCustomers();
            setCustomers(custRes.data || []);
        } catch (err) {
            console.error(err);
            notification.error({ message: 'Error', description: 'Failed to load order details' });
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div style={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spin size="large" /></div>;
    if (!order) return <Empty description="Order not found" style={{ marginTop: 100 }} />;

    const milestoneGroups = [
        {
            label: 'Preparation (Milestone 1)',
            icon: ClipboardList,
            color: '#1890ff',
            items: [
                { key: 'docsReceivedForNP', label: 'Docs Received (NP)' },
                { key: 'docsReceivedForJanSamarth', label: 'Docs Received (JanSamarth)' },
                { key: 'registrationOnNPDone', label: 'Registration Done (NP)', dateKey: 'registrationOnNPDate' },
                { key: 'emailIdChangeOnMSEB', label: 'Email ID Updated on MSEB' },
                { key: 'registrationOnJanSamarthDone', label: 'Registration Done (JanSamarth)', dateKey: 'registrationOnJanSamarthDate' }
            ]
        },
        {
            label: 'Approvals & Bank (Milestone 2)',
            icon: ShieldCheck,
            color: '#722ed1',
            items: [
                { key: 'sanctionLetterReceived', label: 'Sanction Letter Received', extraFieldKey: 'sanctionNumber', dateKey: 'sanctionLetterDate' },
                { key: 'docsSentToBank', label: 'Docs Sent to Bank' },
                { key: 'loanAmountReceived', label: 'Loan Amount Received / Advance' }
            ]
        },
        {
            label: 'Installation (Milestone 3)',
            icon: Hammer,
            color: '#fa8c16',
            items: [
                { key: 'structureOrdered', label: 'Structure Ordered', dateKey: 'structureOrderedDate' },
                { key: 'materialDispatched', label: 'Material Dispatched', dateKey: 'materialDispatchedDate' },
                { key: 'geoTaggedPhoto', label: 'GeoTagged Photo Uploaded' },
                { key: 'installationCompleted', label: 'Installation Completed', dateKey: 'installationCompletedDate' }
            ]
        },
        {
            label: 'Grid Connection (Milestone 4)',
            icon: Zap,
            color: '#52c41a',
            items: [
                { key: 'genMeterTested', label: 'Gen Meter Tested' },
                { key: 'msebDocsSubmitted', label: 'MSEB Docs Submitted' },
                { key: 'releaseNoteReceived', label: 'Release Note Received' },
                { key: 'metersConnected', label: 'Meters Connected' },
                { key: 'metersPunched', label: 'Meters Punched' }
            ]
        },
        {
            label: 'Close Out (Milestone 5)',
            icon: Flag,
            color: '#eb2f96',
            items: [
                { key: 'bankStatementObtained', label: 'Bank Statement Obtained' },
                { key: 'dcrCertificateReady', label: 'DCR Certificate Ready' },
                { key: 'installationDetailsFilledOnNP', label: 'Install Details on NP' },
                { key: 'inspectionApprovedByMSEB', label: 'Inspection Approved (MSEB)' },
                { key: 'appliedForSubsidy', label: 'Applied for Subsidy' }
            ]
        },
        {
            label: 'Post Installation (Milestone 6)',
            icon: SafetyCertificateOutlined,
            color: '#722ed1',
            items: [
                { key: 'panelWarranty', label: 'Warranty for Panels' },
                { key: 'inverterWarranty', label: 'Warranty for Inverters' }
            ]
        }
    ];

    const totalMilestones = milestoneGroups.reduce((acc, g) => acc + g.items.length, 0);

    const completedCount = milestoneGroups.reduce((count, group) => {
        return count + group.items.filter(item => !!order[item.key]).length;
    }, 0);
    const progress = Math.round((completedCount / totalMilestones) * 100);

    const getStatusForGroup = (group) => {
        const done = group.items.filter(item => !!order[item.key]).length;
        if (done === group.items.length) return 'Completed';
        if (done > 0) return 'In Progress';
        return 'Pending';
    };

    return (
        <div style={{ padding: '24px 40px', background: '#f8fafc', minHeight: '100vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Space size="middle">
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/orders')} />
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Title level={4} style={{ margin: 0 }}>Order Detail: #{order.id}</Title>
                            <Tag color={order.projectStatus === 'Completed' ? 'green' : 'blue'}>{order.projectStatus}</Tag>
                        </div>
                        <Text type="secondary">{dayjs(order.orderDate).format('MMMM D, YYYY')}</Text>
                    </div>
                </Space>
                <Space>
                    <Button
                        icon={<FileTextOutlined />}
                        onClick={() => setIsProformaVisible(true)}
                        size="large"
                    >
                        Generate Proforma
                    </Button>
                    <Button type="primary" size="large" icon={<EditOutlined />} onClick={() => navigate(`/orders?edit=${order.id}`)}>Edit Order</Button>
                </Space>
            </div>

            <div style={{ marginBottom: 24 }}>
                {(() => {
                    const alerts = [];
                    const today = dayjs();
                    const orderDate = dayjs(order.orderDate);
                    const daysSinceOrder = today.diff(orderDate, 'day');

                    // Helper to count completed items in a group
                    const isGroupCompleted = (groupIndex) => {
                        const group = milestoneGroups[groupIndex];
                        const done = group.items.filter(item => !!order[item.key]).length;
                        return done === group.items.length;
                    };

                    // Milestone 1: Preparation (10 days from order date)
                    if (!isGroupCompleted(0) && daysSinceOrder > 10 && order.projectStatus !== 'Completed') {
                        alerts.push({
                            type: 'warning',
                            message: `Preparation (Milestone 1) Alert: Preparation is not complete after 10 days of the Order Date.`
                        });
                    }

                    // Milestone 2: Approvals & Bank (15 days from Registration Done NP)
                    if (!isGroupCompleted(1) && order.registrationOnNPDate && order.projectStatus !== 'Completed') {
                        const daysSinceRegNP = today.diff(dayjs(order.registrationOnNPDate), 'day');
                        if (daysSinceRegNP > 15) {
                            alerts.push({
                                type: 'warning',
                                message: `Approvals & Bank (Milestone 2) Alert: Not completed after 15 days of Registration Done (NP).`
                            });
                        }
                    }

                    // Milestone 3: Installation (15 days after loan amount received)
                    // (Assuming loanAmountReceived is a boolean. If it's just a boolean, we don't have a date for it in schema, 
                    // but wait, schema says: `loanAmountReceived Boolean`. There is no `loanAmountReceivedDate`.
                    // Let's check schema for loan amount date.)
                    // *Self-correction based on schema checked earlier*: There is no loanAmountReceivedDate.
                    // Let's assume the alert condition is "loanAmountReceived is true, and it has been 15 days since... wait, we need a date.
                    // Let's use `latestActivity` or updated date if we can't find a specific date, OR we can check if it's been 15 days since Approvals & Bank completed?
                    // Actually, let's look at the items again.
                    // Milestone 2 items: sanctionLetterReceived, docsSentToBank, loanAmountReceived.
                    // If Milestone 2 is completed, it means loanAmountReceived is true.
                    // We don't have a specific `loanAmountReceivedDate`. We might have to use `updatedAt` when it was set, or just use the system's `updatedAt`.
                    // Let's implement it based on `updatedAt` for now, or just the fact that it's been 15 days since Milestone 2 completed.
                    // Wait, if loan amount is received, Milestone 3 should be completed within 15 days.
                    // Since I don't have `loanAmountReceivedDate`, if `order.loanAmountReceived` is true and 15 days have passed since `sanctionLetterDate`?
                    // I will check the schema again, let's keep the exact logic to what we can do with current fields.
                    // If no date field, I'll fallback to `updatedAt` or skip it if it's impossible to calculate accurately without backend changes.
                    // Let's check what date fields we have: orderDate, registrationOnNPDate, registrationOnJanSamarthDate, sanctionLetterDate, structureOrderedDate, materialDispatchedDate, installationCompletedDate.
                    // We don't have `loanAmountReceivedDate`. I'll use `sanctionLetterDate` as a proxy if we don't have it, or just use `updatedAt`.
                    // Actually, I should use `sanctionLetterDate` + some offset if it's the best proxy, or I can just check if loanAmountReceived is true and it's been 15 days since the last milestone date.
                    // Let me review the schema for date fields.
                    // Let's just create the logic assuming `updatedAt` for now, or use `sanctionLetterDate` which is usually close.
                    // Actually, I will just calculate days since `sanctionLetterDate` for Milestone 3 if loanAmountReceivedDate doesn't exist, but that's a guess.
                    // Let's just use `sanctionLetterDate` for now for the 15 days after loan amount received, as it's the closest date field in that group.
                    if (!isGroupCompleted(2) && order.loanAmountReceived && order.sanctionLetterDate && order.projectStatus !== 'Completed') {
                        const daysSinceSanction = today.diff(dayjs(order.sanctionLetterDate), 'day');
                        if (daysSinceSanction > 15) { // Proxying loan receipt to sanction letter date
                            alerts.push({
                                type: 'warning',
                                message: `Installation (Milestone 3) Alert: Not completed 15 days after Loan Amount Received (proxy: Sanction Letter Date).`
                            });
                        }
                    }

                    // Milestone 4: Grid Connection (15 days after installation complete date)
                    if (!isGroupCompleted(3) && order.installationCompletedDate && order.projectStatus !== 'Completed') {
                        const daysSinceInstall = today.diff(dayjs(order.installationCompletedDate), 'day');
                        if (daysSinceInstall > 15) {
                            alerts.push({
                                type: 'warning',
                                message: `Grid Connection (Milestone 4) Alert: Not completed 15 days after Installation Completed Date.`
                            });
                        }
                    }

                    // Milestone 5: Close Out (15 days after meters punched)
                    // `metersPunched` is a boolean, no `metersPunchedDate` in schema.
                    // Let's proxy to `updatedAt` or `installationCompletedDate` + 15 maybe?
                    // Let's use `updatedAt` if `metersPunched` is true.
                    if (!isGroupCompleted(4) && order.metersPunched && order.projectStatus !== 'Completed') {
                        const daysSinceMetersPunched = today.diff(dayjs(order.updatedAt), 'day'); // Proxying via updatedAt
                        if (daysSinceMetersPunched > 15) {
                            alerts.push({
                                type: 'warning',
                                message: `Close Out (Milestone 5) Alert: Not completed 15 days after Meters Punched.`
                            });
                        }
                    }

                    // Milestone 6: Post Installation (10 days after applied for subsidy)
                    // `appliedForSubsidy` is a boolean, no `appliedForSubsidyDate`.
                    // Proxy via `updatedAt`
                    if (!isGroupCompleted(5) && order.appliedForSubsidy && order.projectStatus !== 'Completed') {
                        const daysSinceSubsidy = today.diff(dayjs(order.updatedAt), 'day');
                        if (daysSinceSubsidy > 10) {
                            alerts.push({
                                type: 'warning',
                                message: `Post Installation (Milestone 6) Alert: Not completed 10 days after Applied for Subsidy.`
                            });
                        }
                    }

                    // Stagnation Case (Keep original logic)
                    const milestoneDateFields = [
                        'registrationOnNPDate', 'registrationOnJanSamarthDate', 'sanctionLetterDate',
                        'materialDispatchedDate', 'installationCompletedDate',
                        'createdAt', 'updatedAt'
                    ];

                    const dates = milestoneDateFields
                        .map(key => order[key] ? dayjs(order[key]) : null)
                        .filter(d => d !== null);

                    const latestActivity = dates.length > 0 ? dayjs.max(dates) : orderDate;
                    const daysSinceActivity = today.diff(latestActivity, 'day');

                    if (progress > 0 && progress < 100 && daysSinceActivity > 15 && order.projectStatus !== 'Completed') {
                        alerts.push({
                            type: 'info',
                            message: `Stagnation Alert: No milestone date updates for the last ${daysSinceActivity} days.`
                        });
                    }

                    if (progress === 100 && order.projectStatus !== 'Completed') {
                        alerts.push({
                            type: 'success',
                            message: 'Finalization: All milestones met. Please review and close the order.'
                        });
                    }

                    return alerts.map((alert, idx) => (
                        <Alert
                            key={idx}
                            message={alert.message}
                            type={alert.type}
                            showIcon
                            style={{ borderRadius: 12, marginBottom: 8 }}
                        />
                    ));
                })()}
            </div>

            <Row gutter={[24, 24]}>
                <Col span={17}>
                    <Card
                        title={<Space><ClockCircleOutlined /> Order Timeline</Space>}
                        extra={<Tag color="blue" style={{ borderRadius: 12 }}>{progress}% Complete</Tag>}
                        style={{ borderRadius: 20, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
                    >
                        <div style={{ marginBottom: 32 }}>
                            <Progress percent={progress} status="active" strokeColor={{ '0%': '#1890ff', '100%': '#52c41a' }} strokeWidth={8} />
                        </div>

                        <Timeline
                            mode="left"
                            items={milestoneGroups.map((group) => {
                                const status = getStatusForGroup(group);
                                const Icon = group.icon;
                                const isCompleted = status === 'Completed';
                                const isInProgress = status === 'In Progress';

                                return {
                                    color: isCompleted ? '#52c41a' : (isInProgress ? '#1890ff' : '#d9d9d9'),
                                    dot: <div style={{
                                        padding: 6,
                                        background: isCompleted ? '#f6ffed' : (isInProgress ? '#e6f7ff' : '#fff'),
                                        border: `1px solid ${isCompleted ? '#52c41a' : (isInProgress ? '#1890ff' : '#d9d9d9')}`,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <Icon size={14} color={isCompleted ? '#52c41a' : (isInProgress ? '#1890ff' : '#bfbfbf')} />
                                    </div>,
                                    children: (
                                        <div style={{ marginBottom: 32 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                                <Text strong style={{ fontSize: 16, color: isCompleted ? '#262626' : (isInProgress ? '#1890ff' : '#8c8c8c') }}>
                                                    {group.label}
                                                </Text>
                                                {isCompleted ? (
                                                    <Tag icon={<CheckCircleFilled />} color="success" style={{ borderRadius: 10 }}>Completed</Tag>
                                                ) : isInProgress ? (
                                                    <Tag icon={<ClockCircleOutlined spin />} color="processing" style={{ borderRadius: 10 }}>In Progress</Tag>
                                                ) : null}
                                            </div>
                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                                gap: '12px'
                                            }}>
                                                {group.items.map((item, iIdx) => {
                                                    const isDone = !!order[item.key];
                                                    const dateVal = item.dateKey ? order[item.dateKey] : null;
                                                    const extraVal = item.extraFieldKey ? order[item.extraFieldKey] : null;

                                                    return (
                                                        <div key={iIdx} style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 4,
                                                            padding: '10px 12px',
                                                            background: isDone ? '#fff' : 'rgba(0,0,0,0.01)',
                                                            borderRadius: 10,
                                                            border: `1px solid ${isDone ? '#f0f0f0' : 'transparent'}`,
                                                            boxShadow: isDone ? '0 2px 4px rgba(0,0,0,0.02)' : 'none',
                                                            transition: 'all 0.3s'
                                                        }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                {isDone ?
                                                                    <CheckCircle2 size={14} color="#52c41a" /> :
                                                                    <div style={{ width: 14, height: 14, borderRadius: '50%', border: '1px solid #d9d9d9' }} />
                                                                }
                                                                <Text style={{
                                                                    fontSize: 12,
                                                                    color: isDone ? '#262626' : '#bfbfbf',
                                                                    fontWeight: isDone ? 500 : 400
                                                                }}>
                                                                    {item.label}
                                                                </Text>
                                                            </div>
                                                            {isDone && dateVal && (
                                                                <div style={{ marginLeft: 22 }}>
                                                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                                                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                                                                        {dayjs(dateVal).format('DD MMM, YYYY')}
                                                                    </Text>
                                                                </div>
                                                            )}
                                                            {isDone && extraVal && (
                                                                <div style={{ marginLeft: 22 }}>
                                                                    <Tag color="orange" style={{ fontSize: 10, borderRadius: 4 }}>
                                                                        {extraVal}
                                                                    </Tag>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )
                                };
                            })}
                        />
                    </Card>

                    <Card title="Line Items" style={{ marginTop: 24, borderRadius: 12 }}>
                        <Table
                            dataSource={order.items || []}
                            pagination={false}
                            rowKey="id"
                            size="small"
                            columns={[
                                { title: 'Description', dataIndex: 'description', render: (text, rec) => <div><Text strong>{text || rec.item?.itemName}</Text><br /><Tag color="blue" style={{ fontSize: 10 }}>{rec.natureKey}</Tag></div> },
                                { title: 'Qty', dataIndex: 'quantity', align: 'center', width: 80 },
                                { title: 'Amount', dataIndex: 'totalAmount', align: 'right', width: 120, render: val => `₹${val?.toLocaleString()}` }
                            ]}
                        />
                        <div style={{ textAlign: 'right', marginTop: 24, padding: '16px 24px', background: '#fafafa', borderRadius: 16, border: '1px solid #f0f0f0' }}>
                            <Row gutter={24} justify="end">
                                <Col>
                                    <Statistic title="Total Contract Value" value={order.orderValue} prefix="₹" precision={2} valueStyle={{ color: '#1890ff' }} />
                                </Col>
                                <Col>
                                    <Statistic
                                        title="Total Paid"
                                        value={(order.milestones || []).reduce((sum, m) => sum + (m.amount || 0), 0)}
                                        prefix="₹"
                                        precision={2}
                                        valueStyle={{ color: '#52c41a' }}
                                    />
                                </Col>
                                <Col>
                                    <Statistic
                                        title="Remaining Balance"
                                        value={order.orderValue - (order.milestones || []).reduce((sum, m) => sum + (m.amount || 0), 0)}
                                        prefix="₹"
                                        precision={2}
                                        valueStyle={{ color: '#ff4d4f' }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Card>
                </Col>

                <Col span={7}>
                    <Card title={<Space><UserOutlined /> Customer Info</Space>} style={{ borderRadius: 12, marginBottom: 24 }}>
                        <Descriptions column={1} size="small" colon={false}>
                            <Descriptions.Item label={<Text type="secondary">Name</Text>}><Text strong>{order.customer?.customerName}</Text></Descriptions.Item>
                            <Descriptions.Item label={<Text type="secondary">Mobile</Text>}><Text strong>{order.customer?.customerMobile}</Text></Descriptions.Item>
                            <Descriptions.Item label={<Text type="secondary">Consumer #</Text>}><Tag color="orange">{order.selectedConsumerNumber}</Tag></Descriptions.Item>
                            <Descriptions.Item label={<Text type="secondary">Address</Text>}><Text style={{ fontSize: 12 }}>{order.customer?.customerAddress}</Text></Descriptions.Item>
                        </Descriptions>
                    </Card>

                    <Card title={<Space><BarChartOutlined /> Technical Specs</Space>} style={{ borderRadius: 12 }}>
                        <Descriptions column={1} size="small" colon={false}>
                            <Descriptions.Item label={<Text type="secondary">System Size</Text>}><Text strong>{order.plantSizeKw} kW</Text></Descriptions.Item>
                            <Descriptions.Item label={<Text type="secondary">Structure</Text>}><Text strong>{order.structureType}</Text></Descriptions.Item>
                            <Descriptions.Item label={<Text type="secondary">Classification</Text>}><Text strong>{order.projectType}</Text></Descriptions.Item>
                            <Descriptions.Item label={<Text type="secondary">Connection</Text>}><Tag color="purple">{order.connectionType || 'SinglePhase'}</Tag></Descriptions.Item>
                            <Descriptions.Item label={<Text type="secondary">Channel Partner</Text>}><Text strong>{order.channelPartner?.partnerName || 'N/A'}</Text></Descriptions.Item>
                        </Descriptions>
                    </Card>

                    <Card title={<Space><FileTextOutlined /> Remarks</Space>} style={{ borderRadius: 12, marginTop: 24 }}>
                        {order.remarks ? (
                            <Text style={{ fontSize: 13, color: '#595959', whiteSpace: 'pre-wrap' }}>
                                {order.remarks}
                            </Text>
                        ) : (
                            <Text type="secondary" italic style={{ fontSize: 13 }}>No remarks added.</Text>
                        )}
                    </Card>
                </Col>
            </Row>

            <ProformaGenerator
                visible={isProformaVisible}
                onCancel={() => setIsProformaVisible(false)}
                orders={order ? [order] : []}
                customers={customers}
            />
        </div>
    );
};

export default OrderDetail;
